import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { Button, Card, CardContent, CardHeader, FormHelperText, Grid, Typography } from "@mui/material";
import { useGetAllProfesionalActivitiesQuery } from "redux/api/profesionalActivtyApi";
import { usePutOperationMutation } from "redux/api/operationApi";
import { setCurrentOperation } from "redux/features/operationSlice";
import { set } from "lodash";
import { useIntl } from "react-intl";



const ProfessionalActivityDropdown: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  // Mapeo de literales a etiquetas
  const activityLabels: { [key: string]: string } = {
    AUTONOMOUS: formatMessage({ id: "professional.activity.autonomus" }),
    RENTIER: formatMessage({ id: "professional.activity.rentier" }),
    STUDENT: formatMessage({ id: "professional.activity.student" }),
    RETIRED: formatMessage({ id: "professional.activity.retired" }),
    EMPLOYED: formatMessage({ id: "professional.activity.employed" }),
  };
  //api
  const {
    data: activities,
    isLoading,
    error: apiError,
  } = useGetAllProfesionalActivitiesQuery();
  const [
    updateOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      error: updateError,
    },
  ] = usePutOperationMutation();

  //consts
  const [activity, setActivity] = useState("");
  const [initialActivity, setInitialActivity] = useState<string | null>(null);
  const [error, setError] = useState(false);

  //redux state
  const operation = useSelector((state: any) => {
    return state.operationState?.currentOperation?.data || state.operationState?.currentOperation;
  });
  useEffect(() => {
    if (operation?.professionalActivity) {
      setActivity(operation.professionalActivity);
      setInitialActivity(operation.professionalActivity);
    }
  }, [operation]);
  if (!operation) return <p>{formatMessage({ id: "professional.activity.error" })}</p>;


  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setActivity(event.target.value);
  };

  const handleSubmit = () => {
    if (activity === "") {
      setError(true);
      return;
    }
    if (activity === initialActivity) {
      nextStep();
      return;
    }
    const updatedOperation = {
      ...operation,
      professionalActivity: activity,
    };
    const formData = new FormData();
    formData.append("data", JSON.stringify(updatedOperation));
    updateOperation({ id: operation.id, operationToUpdate: formData }).then(
      (res) => {
        if ("data" in res) {
          dispatch(setCurrentOperation(res.data));
          nextStep();
        }
      }
    );
  };

  if (apiError) {
    console.log("Error:", apiError);
    return <div>{JSON.stringify(apiError)}</div>;
  }
  if (isPostedOperationSuccess && postedOperationData) {
    console.log("Operacion creada:", postedOperationData);
    nextStep();
  }
  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {formatMessage({ id: "professional.activity.title" })}
          </Typography>
        }
      />
      <CardContent>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "start",
              width: "100%",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <FormControl
              error
              sx={{
                width: "100%",
                borderRadius: "2px",
                padding: "5px",
              }}
            >
              <Select
                value={activity}
                onChange={handleSelectChange}
                displayEmpty
                sx={{
                  border: "1.5px solid black",
                  width: "100%",
                  backgroundColor: "white", // Establecer fondo blanco
                  "& .MuiSelect-root": {
                    backgroundColor: "white", // Fondo para la raíz del select
                  },
                  "&:hover": {
                    backgroundColor: "white", // Fondo al pasar el mouse
                  },
                  '&.Mui-focused': {
                    border: '1.5px solid black', // Elimina el borde rojo en el estado focused
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none', // Si usas `OutlinedInput`, asegúrate de eliminar el borde extra
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1px solid black", // Borde del menú desplegable
                      borderRadius: "5px",
                      marginTop: "4px",
                      boxShadow: "0px 4px 6px rgba(0,0,0,0.1)", // Sombra para un diseño más limpio
                      backgroundColor: "white", // Establecer fondo blanco
                      "& .MuiSelect-root": {
                        backgroundColor: "white", // Fondo para la raíz del select
                      },
                      "&:hover": {
                        backgroundColor: "white", // Fondo al pasar el mouse
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <Typography sx={{ color: "#B3B3B3" }}>
                    {formatMessage({ id: "professional.activity.select.title" })}
                  </Typography>
                </MenuItem>
                {isLoading ? (
                  <MenuItem disabled>{formatMessage({ id: "professional.activity.loading" })}Cargando...</MenuItem>
                ) : apiError ? (
                  <MenuItem disabled>{formatMessage({ id: "professional.activity.error.loading" })}Error al cargar las actividades</MenuItem>
                ) : activities ? (
                  activities.map((activityOption) => (
                    <MenuItem key={activityOption._id} value={activityOption._id}>
                      {activityLabels[activityOption.name]}
                    </MenuItem>
                  ))
                ) : null}
              </Select>
              {error && (
                <FormHelperText>
                  {formatMessage({ id: "professional.activity.select.placeholder" })}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                  onClick={handleSubmit}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfessionalActivityDropdown;
